import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Visibility as ViewIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

function TaskStatusCard({ task, onRunNow, onToggle, onViewLogs }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'running':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <SuccessIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatSchedule = (cronSchedule) => {
    // Convert cron to human-readable format
    const scheduleMap = {
      '0 1 * * *': 'Daily at 1:00 AM',
      '0 2 * * *': 'Daily at 2:00 AM',
      '0 3 * * *': 'Daily at 3:00 AM',
      '0 4 * * *': 'Daily at 4:00 AM',
      '*/15 * * * *': 'Every 15 minutes',
      '*/5 * * * *': 'Every 5 minutes',
      '0 */6 * * *': 'Every 6 hours'
    };
    
    return scheduleMap[cronSchedule] || cronSchedule;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h6" component="div">
            {task.name}
          </Typography>
          <Chip
            icon={getStatusIcon(task.lastStatus)}
            label={task.lastStatus || 'Unknown'}
            color={getStatusColor(task.lastStatus)}
            size="small"
          />
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Schedule:</strong> {formatSchedule(task.schedule)}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Last Run:</strong> {formatDate(task.lastRun)}
          </Typography>
          {task.nextRun && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Next Run:</strong> {formatDate(task.nextRun)}
            </Typography>
          )}
        </Box>

        {task.lastError && (
          <Box
            sx={{
              p: 1,
              bgcolor: 'error.light',
              borderRadius: 1,
              mb: 2
            }}
          >
            <Typography variant="caption" color="error.dark">
              <strong>Error:</strong> {task.lastError}
            </Typography>
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={task.enabled}
              onChange={(e) => onToggle(e.target.checked)}
              color="primary"
            />
          }
          label={task.enabled ? 'Enabled' : 'Disabled'}
        />
      </CardContent>

      <CardActions>
        <Tooltip title="Run task now">
          <Button
            size="small"
            startIcon={<PlayIcon />}
            onClick={onRunNow}
            disabled={!task.enabled}
          >
            Run Now
          </Button>
        </Tooltip>
        {onViewLogs && (
          <Tooltip title="View logs">
            <IconButton size="small" onClick={onViewLogs}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
}

export default TaskStatusCard;

